import type { ReactNode } from 'react'
import { CircleAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * A failed fetch is a rendered state, never an indefinite skeleton. Every hook here is a fetch
 * that can fail, and each consumer owes the reader an error branch — this is that branch.
 */
export function ErrorState({ title = 'Could not load this', body, onRetry, className }: {
    title?: string; body?: string; onRetry?: () => void; className?: string
}) {
    return (
        <div role="alert" className={cn('rounded-xl border border-destructive/30 bg-destructive/5 p-6', className)}>
            <div className="flex items-start gap-3">
                <CircleAlert className="mt-0.5 size-5 shrink-0 text-destructive" aria-hidden="true" />
                <div className="flex-1">
                    <p className="font-medium">{title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                        {body ?? 'Something went wrong on our side. Try again in a moment.'}
                    </p>
                    {onRetry && <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>Try again</Button>}
                </div>
            </div>
        </div>
    )
}

/**
 * What a screen shows before it has data. Deliberately explicit: an empty table that looks broken
 * and an empty table that is simply empty need different words.
 */
export function EmptyState({ title, body, action, className }: { title: string; body: string; action?: ReactNode; className?: string }) {
    return (
        <div className={cn('rounded-xl border border-dashed border-border px-6 py-12 text-center', className)}>
            <p className="font-medium">{title}</p>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">{body}</p>
            {action && <div className="mt-5 flex justify-center">{action}</div>}
        </div>
    )
}
